import React, { useState, useEffect } from 'react';
import { TimeEntry } from '../types/time-entry';
import { Project } from '../types/project';
import { Task } from '../types/task';
import { User } from '../types/user';
import { WeeklyPlan } from '../types/weekly-plan';
import { NonProductiveCategory } from '../types/non-productive-category';
import { getTimeEntriesByDateRange, createTimeEntry, deleteTimeEntry } from '../api/time-entries';
import { getProjects } from '../api/projects';
import { getTasks } from '../api/tasks';
import { getAllUsers } from '../api/users';
import { getWeeklyPlansByUser } from '../api/weekly-plans';
import { getNonProductiveCategories } from '../api/non-productive-categories';
import Card from '../components/Card';
import WeekSelection from '../components/timesheets/WeekSelection';
import QuickTimeEntryForm from '../components/timesheets/QuickTimeEntryForm';
import WeeklyCalendarDay from '../components/timesheets/WeeklyCalendarDay';
import WeeklySummaryCard from '../components/timesheets/WeeklySummaryCard';
import TimeEntriesList from '../components/timesheets/TimeEntriesList';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { showToast } from '../utils/toast';

const TimesheetsPage: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<NonProductiveCategory[]>([]);
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlan | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Quick entry form state
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [entryDate, setEntryDate] = useState('');
  const [entryProjectId, setEntryProjectId] = useState<string | null>(null);
  const [entryCategoryId, setEntryCategoryId] = useState<string | null>(null);
  const [entryTaskId, setEntryTaskId] = useState<string | null>(null);
  const [entryHours, setEntryHours] = useState(0);
  const [entryDescription, setEntryDescription] = useState('');
  const [entryBillable, setEntryBillable] = useState(true);

  // Get current week (Monday-Friday)
  const getCurrentWeek = (): { start: string; end: string } => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);

    return {
      start: monday.toISOString().split('T')[0],
      end: friday.toISOString().split('T')[0],
    };
  };

  // Load initial data
  useEffect(() => {
    loadData();
    // run only once on mount — loadData is stable for this usage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load time entries when user or week changes
  useEffect(() => {
    if (selectedUserId !== null && selectedWeek.start && selectedWeek.end) {
      loadTimeEntries();
      loadWeeklyPlan();
    }
    // selectedWeek is an object; explicitly ignore exhaustive-deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, selectedWeek.start, selectedWeek.end]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedProjects, fetchedTasks, fetchedUsers, fetchedCategories] = await Promise.all([
        getProjects(),
        getTasks(),
        getAllUsers(),
        getNonProductiveCategories(),
      ]);

      setProjects(fetchedProjects);
      setTasks(fetchedTasks);
      setUsers(fetchedUsers);
      setCategories(fetchedCategories);

      // Auto-select first user and current week
      if (fetchedUsers.length > 0) {
        setSelectedUserId(fetchedUsers[0].id);
      }
      const week = getCurrentWeek();
      setSelectedWeek(week);
      setEntryDate(new Date().toISOString().split('T')[0]);

      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeEntries = async () => {
    if (!selectedWeek.start || !selectedWeek.end) return;

    try {
      const entries = await getTimeEntriesByDateRange(selectedWeek.start, selectedWeek.end);
      // Filter by selected user
      const userEntries = entries.filter(e => e.user_id === selectedUserId);
      setTimeEntries(userEntries);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load time entries';
      setError(errorMessage);
    }
  };

  const loadWeeklyPlan = async () => {
    if (selectedUserId === null || !selectedWeek.start) return;

    try {
      const plans = await getWeeklyPlansByUser(selectedUserId);
      // Find plan matching selected week
      const matchingPlan = plans.find(p => p.week_start_date === selectedWeek.start);
      setWeeklyPlan(matchingPlan || null);
    } catch (err) {
      console.error('Failed to load weekly plan:', err);
      setWeeklyPlan(null);
    }
  };

  const handleQuickEntry = async () => {
    // Validate mutual exclusivity
    if (entryProjectId && entryCategoryId) {
      setError('Cannot specify both project and category');
      return;
    }
    if (!entryProjectId && !entryCategoryId) {
      setError('Must specify either project or category');
      return;
    }
    if (!entryDate || entryHours <= 0 || selectedUserId === null) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const payload: any = {
        user_id: selectedUserId,
        date: entryDate,
        hours: entryHours,
        billable: entryBillable,
      };

      // Only include project fields if project is selected
      if (entryProjectId) {
        payload.project_id = entryProjectId;
        if (entryTaskId) {
          payload.task_id = entryTaskId;
        }
      }

      // Only include category if selected
      if (entryCategoryId) {
        payload.category_id = entryCategoryId;
      }

      // Only include description if provided
      if (entryDescription) {
        payload.description = entryDescription;
      }

      await createTimeEntry(payload);

      // Reset form
      setEntryProjectId(null);
      setEntryCategoryId(null);
      setEntryTaskId(null);
      setEntryHours(0);
      setEntryDescription('');
      setShowQuickEntry(false);

      // Reload entries
      await loadTimeEntries();
      setError(null);
      showToast.success('Time entry logged successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create time entry';
      setError(errorMessage);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!window.confirm('Delete this time entry?')) return;

    try {
      await deleteTimeEntry(entryId);
      await loadTimeEntries();
      showToast.success('Time entry deleted successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete time entry';
      setError(errorMessage);
      showToast.error(errorMessage);
    }
  };

  const changeWeek = (direction: 'prev' | 'next') => {
    const start = new Date(selectedWeek.start);
    const offset = direction === 'prev' ? -7 : 7;
    start.setDate(start.getDate() + offset);
    const end = new Date(start);
    end.setDate(start.getDate() + 4);

    setSelectedWeek({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    });
  };

  const getWeekDates = (): Date[] => {
    if (!selectedWeek.start) return [];
    const dates: Date[] = [];
    const start = new Date(selectedWeek.start);
    for (let i = 0; i < 5; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getEntriesForDate = (date: Date): TimeEntry[] => {
    const dateStr = date.toISOString().split('T')[0];
    return timeEntries.filter(e => e.date === dateStr);
  };

  const getDailyTotal = (date: Date): number => {
    return getEntriesForDate(date).reduce((sum, e) => sum + parseFloat(String(e.hours)), 0);
  };

  const getWeeklyTotal = (): number => {
    return timeEntries.reduce((sum, e) => sum + parseFloat(String(e.hours)), 0);
  };

  const getPlannedHours = (): number => {
    if (!weeklyPlan) return 0;
    const productiveHours = (weeklyPlan.allocations ?? []).reduce((sum, a) => sum + a.hours, 0);
    const nonProductiveHours = (weeklyPlan.non_productive_allocations || []).reduce((sum, a) => sum + a.hours, 0);
    return productiveHours + nonProductiveHours;
  };

  const getProjectName = (projectId: string): string => {
    if (!projects || !Array.isArray(projects)) return `Project #${projectId}`;
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : `Project #${projectId}`;
  };

  const getTaskTitle = (taskId: string): string => {
    const task = tasks.find(t => t.id === taskId);
    return task ? task.title : `Task #${taskId}`;
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : `Category #${categoryId}`;
  };

  // getUserName removed because it's unused in this page

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDates = getWeekDates();
  const weeklyTotal = getWeeklyTotal();
  const plannedHours = getPlannedHours();
  const variance = weeklyTotal - plannedHours;

  if (loading) {
    return (
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid gap-6">
          <CardSkeleton />
          <CardSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Timesheets</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Log hours worked and track actual vs planned time
        </p>
      </div>

      {error && <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">{error}</div>}

      {/* User & Week Selection */}
      <WeekSelection
        users={users}
        selectedUserId={selectedUserId}
        selectedWeek={selectedWeek}
        formatDate={formatDate}
        onUserChange={setSelectedUserId}
        onWeekChange={changeWeek}
        onToggleQuickEntry={() => setShowQuickEntry(!showQuickEntry)}
        showQuickEntry={showQuickEntry}
      />

      {/* Quick Entry Form */}
      {showQuickEntry && (
        <QuickTimeEntryForm
          entryDate={entryDate}
          entryProjectId={entryProjectId}
          entryCategoryId={entryCategoryId}
          entryTaskId={entryTaskId}
          entryHours={entryHours}
          entryDescription={entryDescription}
          entryBillable={entryBillable}
          projects={projects}
          tasks={tasks}
          categories={categories}
          onDateChange={setEntryDate}
          onProjectChange={(projectId) => {
            setEntryProjectId(projectId);
            setEntryCategoryId(null);
            setEntryTaskId(null);
          }}
          onCategoryChange={(categoryId) => {
            setEntryCategoryId(categoryId);
            setEntryProjectId(null);
            setEntryTaskId(null);
          }}
          onTaskChange={setEntryTaskId}
          onHoursChange={setEntryHours}
          onDescriptionChange={setEntryDescription}
          onBillableChange={setEntryBillable}
          onSubmit={handleQuickEntry}
          onCancel={() => setShowQuickEntry(false)}
        />
      )}

      {/* Week Calendar View */}
      <Card variant="glass" className="mb-6">
        <Card.Header title="Time Logged This Week" />
        <Card.Body>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 md:gap-2">
            {weekDates.map(date => {
              const dayEntries = getEntriesForDate(date);
              const dailyHours = getDailyTotal(date);
              const today = isToday(date);

              return (
                <WeeklyCalendarDay
                  key={date.toISOString()}
                  date={date}
                  entries={dayEntries}
                  dailyTotal={dailyHours}
                  isToday={today}
                  formatDate={formatDate}
                  getProjectName={getProjectName}
                  getCategoryName={getCategoryName}
                />
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Weekly Summary & Plan Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Weekly Totals */}
        <WeeklySummaryCard
          weeklyTotal={weeklyTotal}
          plannedHours={plannedHours}
          variance={variance}
          hasWeeklyPlan={weeklyPlan !== null}
        />

        {/* All Time Entries */}
        <TimeEntriesList
          timeEntries={timeEntries}
          getProjectName={getProjectName}
          getCategoryName={getCategoryName}
          getTaskTitle={getTaskTitle}
          onDeleteEntry={handleDeleteEntry}
        />
      </div>
    </div>
  );
};

export default TimesheetsPage;
